import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, Images, Upload, X, Video } from "lucide-react";
import { uploadFile, fileUrl } from "@/lib/api";
import { isVideoPath } from "@/components/MediaViewer";
import { toast } from "sonner";

const MAX_MB = 50;

export default function MultiMediaUpload({ value = [], onChange, testId = "media", min = 0, minVideos = 0, minPhotos = 0, accept = "image/*,video/*" }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(0);
  const photos = value.filter((p) => !isVideoPath(p)).length;
  const videos = value.length - photos;
  const ok = value.length >= min && videos >= minVideos && photos >= minPhotos;

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const good = files.filter((f) => f.size <= MAX_MB * 1024 * 1024);
    if (good.length < files.length) toast.error(`Some files skipped — max ${MAX_MB}MB each`);
    setBusy(good.length);
    const added = [];
    for (const f of good) {
      try { added.push(await uploadFile(f)); } catch { toast.error(`Upload failed: ${f.name}`); }
      setBusy((b) => b - 1);
    }
    if (added.length) { onChange([...value, ...added]); toast.success(`${added.length} file${added.length > 1 ? "s" : ""} uploaded`); }
  };

  const req = [];
  if (min) req.push(`${min}+ files`);
  if (minPhotos) req.push(`${minPhotos}+ photos`);
  if (minVideos) req.push(`${minVideos}+ video${minVideos > 1 ? "s" : ""}`);

  return (
    <div className="space-y-2" data-testid={`${testId}-multi`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-[#94A3B8]">{req.length ? `Required: ${req.join(", ")}` : "Optional"}</span>
        <span className={`font-mono font-bold ${ok ? "text-emerald-400" : "text-amber-400"}`} data-testid={`${testId}-count`}>
          {photos} photo{photos === 1 ? "" : "s"} · {videos} video{videos === 1 ? "" : "s"}
        </span>
      </div>
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {value.map((p, i) => (
            <div key={p + i} className="relative h-20 rounded border border-emerald-500/50 overflow-hidden bg-black" data-testid={`${testId}-item-${i}`}>
              {isVideoPath(p) ? <video src={fileUrl(p)} muted playsInline preload="metadata" className="w-full h-full object-cover" /> : <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />}
              {isVideoPath(p) && <Video className="w-4 h-4 absolute bottom-1 left-1 text-white drop-shadow" />}
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="absolute top-1 right-1 bg-black/70 rounded-full p-1 hover:bg-black" data-testid={`${testId}-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <input ref={camRef} type="file" accept={accept} capture="environment" className="hidden" onChange={pick} data-testid={`${testId}-camera-input`} />
      <input ref={galRef} type="file" accept={accept} multiple className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" disabled={busy > 0} onClick={() => camRef.current?.click()}
          className="h-14 border-dashed border-2 border-[#28364F] bg-[#131B2A] hover:bg-[#1C263B] text-[#94A3B8]" data-testid={`${testId}-camera-btn`}>
          {busy > 0 ? <Upload className="w-4 h-4 animate-pulse" /> : <Camera className="w-4 h-4" />}<span className="ml-2 text-xs">{busy > 0 ? `Uploading ${busy}…` : "Camera"}</span>
        </Button>
        <Button type="button" variant="outline" disabled={busy > 0} onClick={() => galRef.current?.click()}
          className="h-14 border-dashed border-2 border-[#28364F] bg-[#131B2A] hover:bg-[#1C263B] text-[#94A3B8]" data-testid={`${testId}-gallery-btn`}>
          <Images className="w-4 h-4" /><span className="ml-2 text-xs">From gallery (multi)</span>
        </Button>
      </div>
    </div>
  );
}
